import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, ShoppingBag, Loader2, Search, ScanLine, CheckCircle2, X, Package, Truck, Store } from 'lucide-react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import CloudinaryUploader from '../../components/CloudinaryUploader';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';

const EMPTY = { name: '', description: '', price: '', images: [], event_id: '', variant_label: '', variants: [], stock: 0, delivery_options: 'both', shipping_cost: 0, is_active: true };

export default function MerchAdminPage() {
  const [tab, setTab] = useState('productos');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [codeInput, setCodeInput] = useState('');
  const [deliverResult, setDeliverResult] = useState(null);
  const [orderSearch, setOrderSearch] = useState('');
  const [delivering, setDelivering] = useState(null);

  const refresh = () => {
    api.get('/merch/admin/products').then(r => setProducts(r.data.products || [])).catch(() => {});
    api.get('/merch/admin/orders').then(r => setOrders(r.data.orders || [])).catch(() => {});
    api.get('/events/').then(r => setEvents(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  };
  useEffect(refresh, []);

  const save = async () => {
    if (!form.name.trim() || !(parseFloat(form.price) > 0)) return toast.error('Nombre y precio (>0) obligatorios');
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), shipping_cost: parseFloat(form.shipping_cost) || 0 };
      if (editingId) await api.put(`/merch/admin/products/${editingId}`, payload);
      else await api.post('/merch/admin/products', payload);
      toast.success(editingId ? 'Producto actualizado' : 'Producto creado');
      setForm(EMPTY); setEditingId(null); refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar este producto?')) return;
    try { await api.delete(`/merch/admin/products/${id}`); refresh(); toast.success('Eliminado'); }
    catch { toast.error('Error al eliminar'); }
  };

  const deliver = async (key) => {
    setDelivering(key);
    try {
      const r = await api.post(`/merch/admin/orders/${encodeURIComponent(key)}/deliver`);
      if (r.data.already_delivered) {
        toast.warning('⚠️ Este pedido YA fue entregado');
        setDeliverResult({ ...r.data.order, _already: true });
      } else {
        toast.success('✅ Pedido entregado');
        setDeliverResult(r.data.order);
      }
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Código/pedido no encontrado');
      setDeliverResult({ _not_found: true, code: key });
    } finally { setDelivering(null); }
  };

  const filteredOrders = useMemo(() => {
    const q = orderSearch.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(o =>
      (o.id || '').toLowerCase().includes(q) ||
      (o.pickup_code || '').toLowerCase().includes(q) ||
      (o.buyer_email || '').toLowerCase().includes(q) ||
      (o.buyer_name || '').toLowerCase().includes(q)
    );
  }, [orders, orderSearch]);

  const addVariant = () => setForm({ ...form, variants: [...form.variants, { label: '', stock: 0 }] });
  const setVariant = (i, k, v) => setForm({ ...form, variants: form.variants.map((x, idx) => idx === i ? { ...x, [k]: v } : x) });
  const removeVariant = (i) => setForm({ ...form, variants: form.variants.filter((_, idx) => idx !== i) });

  const statusBadge = (s) => {
    const map = { approved: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/40', pending: 'bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40', rejected: 'bg-red-500/15 text-red-400 border-red-500/40', cancelled: 'bg-zinc-700/50 text-zinc-300 border-zinc-600' };
    return <span className={`text-[9px] px-2 py-0.5 rounded-full border uppercase font-bold ${map[s] || map.cancelled}`}>{s === 'approved' ? 'Pagado' : s === 'pending' ? 'Pendiente' : s === 'rejected' ? 'Rechazado' : s}</span>;
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 max-w-6xl" data-testid="merch-admin-page">
        <h1 className="text-2xl font-black text-white uppercase flex items-center gap-2 mb-1"><ShoppingBag className="text-[#D4AF37]" size={22} /> Merchandising</h1>
        <p className="text-[#71717A] text-sm mb-6">Productos, pedidos y entrega de merch con Mercado Pago</p>

        <div className="flex gap-2 mb-6">
          {[['productos', 'Productos', Package], ['pedidos', `Pedidos (${orders.length})`, Truck]].map(([id, label, Icon]) => (
            <button key={id} onClick={() => setTab(id)} className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${tab === id ? 'bg-[#D4AF37] text-black' : 'bg-[#121212] text-[#A1A1AA] border border-[#27272A]'}`} data-testid={`merch-tab-${id}`}>
              <Icon size={13} /> {label}
            </button>
          ))}
        </div>

        {tab === 'productos' && (
          <div className="space-y-6">
            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-5 space-y-4">
              <h3 className="text-white font-bold">{editingId ? 'Editar producto' : 'Nuevo producto'}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#A1A1AA]">Nombre *</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Remera oficial Chris Luno" className="bg-[#121212] border-[#27272A] text-white" data-testid="merch-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#A1A1AA]">Precio (ARS) *</Label>
                  <Input type="number" min="1" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} className="bg-[#121212] border-[#27272A] text-white" data-testid="merch-price-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A1A1AA]">Descripción</Label>
                <Textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="bg-[#121212] border-[#27272A] text-white" data-testid="merch-description-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-[#A1A1AA]">Imágenes (la primera es la principal)</Label>
                {(form.images || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    {form.images.map((img, idx) => (
                      <div key={img} className="relative">
                        <img src={img} alt="" className={`w-16 h-16 rounded-lg object-cover border ${idx === 0 ? 'border-[#D4AF37]' : 'border-[#27272A]'}`} />
                        {idx === 0 && <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black text-[8px] font-black uppercase px-1.5 rounded">Principal</span>}
                        <button
                          onClick={() => setForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }))}
                          className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                          data-testid={`merch-image-remove-${idx}`}
                        ><X size={10} /></button>
                      </div>
                    ))}
                  </div>
                )}
                <CloudinaryUploader
                  folder="merch"
                  resourceType="image"
                  accept="image/*"
                  maxSize={8 * 1024 * 1024}
                  buttonText={(form.images || []).length > 0 ? 'Agregar otra foto' : 'Subir imagen'}
                  showPreview={false}
                  onUploadComplete={(r) => setForm(prev => ({ ...prev, images: [...(prev.images || []), r.url] }))}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#A1A1AA]">Evento vinculado (opcional)</Label>
                  <select value={form.event_id} onChange={e => setForm({ ...form, event_id: e.target.value })} className="w-full bg-[#121212] border border-[#27272A] rounded-md px-3 py-2 text-white text-sm" data-testid="merch-event-select">
                    <option value="">— Producto general —</option>
                    {events.map(ev => <option key={ev.id} value={ev.id}>{ev.title}</option>)}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#A1A1AA]">Entrega</Label>
                  <select value={form.delivery_options} onChange={e => setForm({ ...form, delivery_options: e.target.value })} className="w-full bg-[#121212] border border-[#27272A] rounded-md px-3 py-2 text-white text-sm" data-testid="merch-delivery-select">
                    <option value="both">Retiro en evento + Envío</option>
                    <option value="pickup">Solo retiro en el evento</option>
                    <option value="shipping">Solo envío a domicilio</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-[#A1A1AA]">Costo de envío (ARS)</Label>
                  <Input type="number" min="0" value={form.shipping_cost} onChange={e => setForm({ ...form, shipping_cost: e.target.value })} disabled={form.delivery_options === 'pickup'} className="bg-[#121212] border-[#27272A] text-white" data-testid="merch-shipping-cost-input" />
                </div>
              </div>

              <div className="bg-[#121212] border border-[#27272A] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <p className="text-white text-sm font-bold">Variantes (talles, colores, etc.)</p>
                    <p className="text-[#52525B] text-xs">Si no cargás variantes, se usa el stock general. Stock 0 = ilimitado.</p>
                  </div>
                  <button onClick={addVariant} className="text-xs px-3 py-1.5 bg-[#D4AF37]/15 text-[#D4AF37] border border-[#D4AF37]/40 rounded-lg font-bold flex items-center gap-1" data-testid="merch-add-variant-btn"><Plus size={12} /> Agregar variante</button>
                </div>
                {form.variants.length > 0 && (
                  <div className="space-y-2">
                    <Input value={form.variant_label} onChange={e => setForm({ ...form, variant_label: e.target.value })} placeholder='Nombre del eje (ej: "Talle", "Color")' className="bg-[#0A0A0A] border-[#27272A] text-white max-w-xs" data-testid="merch-variant-label-input" />
                    {form.variants.map((v, i) => (
                      <div key={i} className="flex gap-2 items-center">
                        <Input value={v.label} onChange={e => setVariant(i, 'label', e.target.value)} placeholder="Ej: M, L, XL, Negro…" className="bg-[#0A0A0A] border-[#27272A] text-white" data-testid={`merch-variant-name-${i}`} />
                        <Input type="number" min="0" value={v.stock} onChange={e => setVariant(i, 'stock', parseInt(e.target.value) || 0)} placeholder="Stock" className="bg-[#0A0A0A] border-[#27272A] text-white w-28" data-testid={`merch-variant-stock-${i}`} />
                        <button onClick={() => removeVariant(i)} className="text-[#71717A] hover:text-red-400 p-1"><Trash2 size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
                {form.variants.length === 0 && (
                  <div className="flex items-center gap-3">
                    <Label className="text-xs text-[#A1A1AA]">Stock general (0 = ilimitado)</Label>
                    <Input type="number" min="0" value={form.stock} onChange={e => setForm({ ...form, stock: parseInt(e.target.value) || 0 })} className="bg-[#0A0A0A] border-[#27272A] text-white w-28" data-testid="merch-stock-input" />
                  </div>
                )}
              </div>

              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 text-xs text-[#A1A1AA] cursor-pointer">
                  <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} /> Publicado en la tienda
                </label>
                <button onClick={save} disabled={saving} className="px-5 py-2.5 bg-[#D4AF37] text-black rounded-lg text-sm font-bold flex items-center gap-2" data-testid="merch-save-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {editingId ? 'Guardar cambios' : 'Crear producto'}
                </button>
                {editingId && <button onClick={() => { setForm(EMPTY); setEditingId(null); }} className="px-4 py-2.5 border border-[#27272A] text-[#A1A1AA] rounded-lg text-sm">Cancelar</button>}
              </div>
            </div>

            <div className="space-y-2">
              {products.length === 0 && <p className="text-sm text-[#52525B] italic">Sin productos todavía.</p>}
              {products.map(p => (
                <div key={p.id} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4" data-testid={`merch-product-row-${p.id}`}>
                  {(p.images || [])[0] && <img src={p.images[0]} alt="" className="w-12 h-12 rounded-lg object-cover" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm flex items-center gap-2 flex-wrap">
                      {p.name}
                      {!p.is_active && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-700 text-zinc-300 uppercase font-bold">Oculto</span>}
                    </p>
                    <p className="text-xs text-[#71717A]">
                      <span className="text-[#D4AF37] font-bold">${Number(p.price).toLocaleString('es-AR')}</span>
                      {' · '}{p.sold_count || 0} vendidos
                      {(p.variants || []).length > 0 ? <span> · {p.variant_label || 'Variantes'}: {p.variants.map(v => `${v.label}(${v.stock === 0 ? '∞' : v.stock})`).join(' ')}</span> : <span> · Stock: {p.stock === 0 ? '∞' : p.stock}</span>}
                      {p.event && <span className="text-sky-400"> · 🎫 {p.event.title}</span>}
                    </p>
                  </div>
                  <button onClick={() => { setForm({ name: p.name, description: p.description || '', price: p.price, images: p.images || [], event_id: p.event_id || '', variant_label: p.variant_label || '', variants: p.variants || [], stock: p.stock || 0, delivery_options: p.delivery_options || 'both', shipping_cost: p.shipping_cost || 0, is_active: p.is_active !== false }); setEditingId(p.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="text-[#71717A] hover:text-white p-2" data-testid={`merch-edit-${p.id}`}><Pencil size={15} /></button>
                  <button onClick={() => remove(p.id)} className="text-[#71717A] hover:text-red-400 p-2" data-testid={`merch-delete-${p.id}`}><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'pedidos' && (
          <div className="space-y-6">
            <div className="bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-xl p-5">
              <h3 className="text-white font-bold flex items-center gap-2 mb-1"><ScanLine size={16} className="text-[#D4AF37]" /> Entregar pedido por código</h3>
              <p className="text-xs text-[#52525B] mb-3">Ingresá el código de retiro (BRVM-XXXXXX) que muestra el cliente. Queda marcado como entregado.</p>
              <div className="flex gap-2">
                <Input value={codeInput} onChange={e => setCodeInput(e.target.value.toUpperCase())} onKeyDown={e => e.key === 'Enter' && codeInput.trim() && deliver(codeInput.trim())} placeholder="BRVM-XXXXXX" className="bg-[#121212] border-[#27272A] text-white font-mono tracking-widest max-w-xs" data-testid="merch-code-input" />
                <button onClick={() => codeInput.trim() ? deliver(codeInput.trim()) : toast.error('Ingresá un código')} className="px-5 py-2 bg-[#D4AF37] text-black rounded-lg text-sm font-bold flex items-center gap-2" data-testid="merch-deliver-btn">
                  {delivering === codeInput.trim() ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Validar y entregar
                </button>
              </div>
              {deliverResult && (
                <div className={`mt-4 rounded-lg p-4 border ${deliverResult._not_found ? 'border-red-500/40 bg-red-500/5' : deliverResult._already ? 'border-amber-500/40 bg-amber-500/5' : 'border-emerald-500/40 bg-emerald-500/5'}`} data-testid="merch-deliver-result">
                  {deliverResult._not_found ? (
                    <p className="text-sm text-red-400 font-bold">❌ "{deliverResult.code}" no encontrado o no pagado</p>
                  ) : (
                    <>
                      <p className={`text-sm font-bold ${deliverResult._already ? 'text-amber-400' : 'text-emerald-400'}`}>
                        {deliverResult._already ? `⚠️ YA entregado${deliverResult.delivered_at ? ` el ${new Date(deliverResult.delivered_at).toLocaleString('es-AR')}` : ''}` : '✅ Pedido entregado'}
                      </p>
                      <p className="text-xs text-[#A1A1AA] mt-1">{deliverResult.buyer_name} · {(deliverResult.items || []).map(i => `${i.name}${i.variant ? ` (${i.variant})` : ''} x${i.quantity}`).join(', ')} · <span className="text-[#D4AF37] font-bold">${Number(deliverResult.total_amount).toLocaleString('es-AR')}</span></p>
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-5">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <h3 className="text-white font-bold">Pedidos ({orders.length})</h3>
                <div className="relative">
                  <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#52525B]" />
                  <Input value={orderSearch} onChange={e => setOrderSearch(e.target.value)} placeholder="Buscar código, email, nombre…" className="bg-[#121212] border-[#27272A] text-white pl-8 h-8 text-xs w-64" data-testid="merch-orders-search" />
                </div>
              </div>
              {filteredOrders.length === 0 ? <p className="text-sm text-[#52525B] italic">Sin pedidos{orderSearch ? ' que coincidan' : ' todavía'}.</p> : (
                <div className="max-h-[32rem] overflow-y-auto space-y-2">
                  {filteredOrders.map(o => (
                    <div key={o.id} className="border border-[#1a1a1a] rounded-lg p-3" data-testid={`merch-order-row-${o.id}`}>
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <p className="text-white text-sm font-semibold flex items-center gap-2 flex-wrap">
                            {o.buyer_name} {statusBadge(o.status)}
                            {o.delivery_method === 'pickup' ? <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/40 uppercase font-bold flex items-center gap-1"><Store size={8} /> Retiro</span> : <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/40 uppercase font-bold flex items-center gap-1"><Truck size={8} /> Envío</span>}
                            {o.delivery_status === 'delivered' && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-zinc-700/60 text-zinc-300 uppercase font-bold">Entregado</span>}
                          </p>
                          <p className="text-[11px] text-[#71717A] mt-0.5">
                            {o.buyer_email} · {(o.items || []).map(i => `${i.name}${i.variant ? ` (${i.variant})` : ''} x${i.quantity}`).join(', ')}
                            {' · '}<span className="text-[#D4AF37] font-bold">${Number(o.total_amount).toLocaleString('es-AR')}</span>
                            {o.pickup_code && <span> · <span className="font-mono text-[#D4AF37] font-bold">{o.pickup_code}</span></span>}
                            {' · '}{new Date(o.created_at).toLocaleDateString('es-AR')}
                          </p>
                          {o.delivery_method === 'shipping' && o.shipping_address && <p className="text-[11px] text-[#52525B] mt-0.5">📦 {o.shipping_address.street}, {o.shipping_address.city}{o.shipping_address.province ? `, ${o.shipping_address.province}` : ''}</p>}
                        </div>
                        {o.status === 'approved' && o.delivery_status !== 'delivered' && (
                          <button onClick={() => window.confirm(`¿Marcar entregado el pedido de ${o.buyer_name}?`) && deliver(o.id)} disabled={delivering === o.id} className="text-[9px] px-2.5 py-1.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/40 uppercase font-bold hover:bg-emerald-500/25" data-testid={`merch-mark-delivered-${o.id}`}>
                            {delivering === o.id ? '…' : 'Entregar'}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
